@use '../../styles/mixins.scss' as mixins;
@use '../../styles/themed' as themed;
@use '../../styles/border' as border;
@use '../../styles/typography.scss' as typography;

.iconButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex-shrink: 0;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition:
    background-color 100ms ease-out,
    color 100ms ease-out,
    transform 100ms ease-out;

  &:hover:not(:disabled) {
    transition:
      background-color 150ms ease,
      color 150ms ease,
      transform 100ms ease-out;
  }

  &:disabled {
    @include mixins.disabled(0.3);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:focus-visible {
    @include mixins.focus-ring;
  }
}

// Sizes
.xs {
  width: 24px;
  height: 24px;
  padding: 0;
  border-radius: 6px;
}

.sm {
  width: 28px;
  height: 28px;
  padding: 0;
}

.md {
  width: 32px;
  height: 32px;
  padding: 0;
}

.lg {
  width: 36px;
  height: 36px;
  padding: 0;
}

// Variants
.clear {
  background-color: transparent;
  color: themed.color(icon-secondary);
  border: 1px solid transparent;

  &:hover:not(:disabled) {
    background-color: themed.color(button-clear-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-clear-active);
  }

  &[aria-pressed='true'] {
    background-color: themed.color(button-clear-active);
    color: themed.color(icon-primary);
  }

  &[aria-pressed='true']:hover:not(:disabled) {
    background-color: themed.color(button-clear-active-hover);
  }

  &:disabled {
    pointer-events: none;
  }
}

.secondary {
  background-color: themed.color(button-secondary-default);
  color: themed.color(icon-secondary);
  border: 1px solid transparent;

  &:hover:not(:disabled) {
    background-color: themed.color(button-secondary-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-secondary-active);
  }

  &[aria-pressed='true'] {
    background-color: themed.color(button-secondary-active);
    color: themed.color(icon-primary);
  }

  &:disabled {
    pointer-events: none;
  }
}

.secondary-accent {
  background-color: themed.color(button-accent-secondary-default);
  color: themed.color(icon-accent);

  &:hover:not(:disabled) {
    background-color: themed.color(button-accent-secondary-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-accent-secondary-active);
  }
}

.accent {
  background-color: themed.color(button-accent-default);
  color: themed.color(icon-on-accent);

  &:hover:not(:disabled) {
    background-color: themed.color(button-accent-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-accent-active);
  }
}

.tertiary {
  background-color: themed.color(button-tertiary-default);
  color: themed.color(icon-inverse);

  &:hover:not(:disabled) {
    background-color: themed.color(button-tertiary-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-tertiary-active);
  }
}

.outline {
  background-color: transparent;
  color: themed.color(icon-secondary);
  box-shadow: inset 0 0 0 border.$border-width themed.color(border-strong);

  &:hover:not(:disabled) {
    background-color: themed.color(surface-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-clear-active);
  }

  &:disabled {
    pointer-events: none;
  }
}

.full {
  background-color: themed.color(button-full-default);
  color: themed.color(icon-secondary);
  box-shadow: inset 0 0 0 border.$border-width themed.color(border-default);

  &:hover:not(:disabled) {
    background-color: themed.color(button-full-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-full-active);
  }

  &:disabled {
    pointer-events: none;
  }
}

.toggleButtonWrapper {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0;
  align-items: center;
  background: transparent;
  border: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
  border-radius: 8px;

  &.withTitle {
    padding: 8px 6px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:focus-visible {
    @include mixins.focus-ring;
    outline: none;
  }

  &:hover:not(:disabled):not([aria-pressed='true']) .iconButton.clear {
    background-color: themed.color(surface-active);
  }

  &[aria-pressed='true']:not(:disabled) .iconButton.clear {
    background-color: themed.color(surface-active);
    color: themed.color(icon-primary);
  }

  &[aria-pressed='true']:hover:not(:disabled) .iconButton.clear,
  &:active:not(:disabled) .iconButton.clear {
    background-color: themed.color(surface-indent);
    color: themed.color(icon-primary);
  }

  &:disabled .iconButton {
    cursor: inherit;
    pointer-events: none;
  }
}

.toggleButtonLabel {
  @include typography.text-s-medium;
  cursor: inherit;
  color: themed.color(text-secondary);
  text-align: center;

  &.active {
    color: themed.color(text-primary);
  }

  &.disabled {
    @include mixins.disabled(0.5);
  }
}
